import React, { useMemo } from "react";
import { TrendingUp, Lock, CheckCircle2 } from "lucide-react";

import { resolveActiveEmployee } from "../../Shared/ScreenUtils.jsx";
import { SALES_LADDER, ENGINEERING_LADDER, ENGINEERING_FLAT, resolveRungIndex } from "../../Shared/GrowthRoadmapContent.js";
import { useCopyProtection } from "../../Shared/useCopyProtection.js";
import "../../Styles/GrowthRoadmapScreen.css";

// Growth Roadmap — one page, same module, different ladder depending on who's
// looking. Sales/BA sees the real L0-L7 ladder the sales org is actually run
// against; Engineering sees the dictated career rungs. Everyone else sees an
// honest "not mapped yet" state rather than a ladder that doesn't apply to
// them. Full-timers see every rung in full detail, end to end, as a
// transparency/reference view; everyone else sees detail up through the rung
// just past where they currently sit, with the rest shown as a heading only
// until a manager moves them up — "locked" means the heading still shows, not
// that the rung disappears.
//
// "Current rung" is a best-effort match against position title, not real
// tracked activity (calls closed, releases shipped) — that data source is the
// planned next step for this page, not built yet. Treated as a hint, not an
// assessment: never shown as "you are below standard", only as "here's where
// the ladder says you probably are today".

function isFullTime(employmentType) {
  return ["full-time", "fulltime", "full time", "ft"].includes(String(employmentType || "").trim().toLowerCase());
}

function RungStat({ label, value }) {
  if (!value) return null;
  return (
    <div className="Growth-Stat">
      <span className="Growth-Stat-Label">{label}</span>
      <span className="Growth-Stat-Value">{value}</span>
    </div>
  );
}

function WallNote({ wall }) {
  if (!wall) return null;
  return (
    <div className="Growth-Wall">
      <strong>{wall.name}</strong>
      <p>{wall.symptom}</p>
      <p className="Growth-Wall-Fix">Fix: {wall.fix}</p>
    </div>
  );
}

function RungCard({ rung, state }) {
  // state: "done" | "current" | "next" | "locked"
  if (state === "locked") {
    return (
      <div className="Growth-Rung Growth-Rung--locked">
        <div className="Growth-Rung-Head">
          <span className="Growth-Rung-Level">{rung.level}</span>
          <span className="Growth-Rung-Title">{rung.title}</span>
          <Lock size={14} strokeWidth={1.8} className="Growth-Lock-Icon" />
        </div>
        {rung.band && <span className="Growth-Rung-BandOnly">{rung.band}</span>}
        <span className="Growth-Rung-LockedNote">Unlocks with your manager — see the proof gate on your current rung first.</span>
      </div>
    );
  }
  return (
    <div className={"Growth-Rung Growth-Rung--" + state}>
      <div className="Growth-Rung-Head">
        <span className="Growth-Rung-Level">{rung.level}</span>
        <span className="Growth-Rung-Title">{rung.title}</span>
        {state === "current" && <span className="Growth-Rung-Badge">You're here</span>}
        {state === "next" && <span className="Growth-Rung-Badge Growth-Rung-Badge--next">Next rung</span>}
        {state === "done" && <CheckCircle2 size={15} strokeWidth={1.8} className="Growth-Done-Icon" />}
      </div>
      {rung.shift && <p className="Growth-Rung-Shift">{rung.shift}</p>}
      <div className="Growth-Rung-Stats">
        <RungStat label="Market" value={rung.band} />
        <RungStat label="Buyer" value={rung.buyer} />
        <RungStat label="ACV" value={rung.acv} />
        <RungStat label="Cycle" value={rung.cycle} />
        <RungStat label="Mix" value={rung.ratio} />
      </div>
      {rung.proofGate && (
        <div className="Growth-ProofGate">
          <strong>Proof gate</strong>
          <span>{rung.proofGate}</span>
        </div>
      )}
      <WallNote wall={rung.wall} />
    </div>
  );
}

function SalesLadder({ positionTitle, fullTime, overrideLevel }) {
  const rungs = SALES_LADDER.rungs;
  const currentIdx = useMemo(() => resolveRungIndex(rungs, positionTitle, overrideLevel), [positionTitle, overrideLevel]);
  const visibleDepth = fullTime ? rungs.length : currentIdx + 2;

  return (
    <div className="Growth-RungList">
      {rungs.map((rung, i) => {
        const state = i < currentIdx ? "done" : i === currentIdx ? "current" : i < visibleDepth ? "next" : "locked";
        return <RungCard key={rung.level} rung={rung} state={state} />;
      })}
    </div>
  );
}

function EngineeringLadder({ positionTitle, fullTime, overrideLevel }) {
  const { rungs, fork, rungsAfterFork } = ENGINEERING_LADDER;
  const matchedFlatIdx = useMemo(() => resolveRungIndex(ENGINEERING_FLAT, positionTitle, overrideLevel), [positionTitle, overrideLevel]);
  // Map the flat match index back onto step order: rungs(0-3) stay as-is, both
  // fork branches collapse onto step 4, the two after-fork rungs become 5/6.
  const stepIndex = matchedFlatIdx <= 3 ? matchedFlatIdx : matchedFlatIdx <= 5 ? 4 : matchedFlatIdx - 1;
  const totalSteps = rungs.length + 1 + rungsAfterFork.length; // 4 + fork + 2
  const visibleDepth = fullTime ? totalSteps : stepIndex + 2;

  const stateFor = (i) => (i < stepIndex ? "done" : i === stepIndex ? "current" : i < visibleDepth ? "next" : "locked");

  return (
    <div className="Growth-RungList">
      {rungs.map((rung, i) => <RungCard key={rung.level} rung={rung} state={stateFor(i)} />)}

      {(() => {
        const forkStep = rungs.length; // index 4
        const forkState = stateFor(forkStep);
        if (forkState === "locked") {
          return (
            <div className="Growth-Rung Growth-Rung--locked">
              <div className="Growth-Rung-Head">
                <span className="Growth-Rung-Level">5</span>
                <span className="Growth-Rung-Title">{fork.label}</span>
                <Lock size={14} strokeWidth={1.8} className="Growth-Lock-Icon" />
              </div>
              <span className="Growth-Rung-LockedNote">Unlocks with your manager — see the proof gate on your current rung first.</span>
            </div>
          );
        }
        return (
          <div className="Growth-Fork">
            <p className="Growth-Fork-Label">{fork.label}</p>
            <div className="Growth-Fork-Branches">
              {fork.branches.map((branch) => (
                <RungCard key={branch.level} rung={branch} state={forkState} />
              ))}
            </div>
          </div>
        );
      })()}

      {rungsAfterFork.map((rung, i) => <RungCard key={rung.level} rung={rung} state={stateFor(rungs.length + 1 + i)} />)}
    </div>
  );
}

function NotMappedYet({ family }) {
  return (
    <div className="ds-card Growth-EmptyState">
      <p>
        {family ? `${family}'s` : "Your team's"} growth roadmap isn't built yet — Sales and Engineering are live first.
        Ask your manager if you want to see where your work would slot into either one.
      </p>
    </div>
  );
}

export default function GrowthRoadmapScreen({ data }) {
  // Same reasoning as Playbook: this carries real promotion proof gates
  // and market-band detail, company IP worth the same friction against
  // casual copying. Superusers stay unrestricted.
  useCopyProtection(!!data?.me?.user?.isSuperuser);

  const employee = resolveActiveEmployee(data) || {};
  // Read straight off the employee's own row, not the Departments list --
  // that list is gated behind hrms read access, which Juniors/Interns don't
  // have, and this page is explicitly open to every employment type.
  const family = employee.department_family || "";
  const fullTime = isFullTime(employee.employment_type);
  const positionTitle = employee.position_title || "";

  const ladder = family === "Sales/BA" ? SALES_LADDER : family === "Engineering" ? ENGINEERING_LADDER : null;

  return (
    <section className="Screen-Stack Growth-Screen">
      <header className="ds-page-header">
        <div>
          <span className="ds-page-header__eyebrow">My Space</span>
          <h1 className="ds-page-header__title">
            <TrendingUp size={22} strokeWidth={1.8} style={{ verticalAlign: "-3px" }} /> Growth Roadmap
          </h1>
          <p className="ds-page-header__sub">
            {ladder ? ladder.subtitle : "Where you sit today, and what the next rung actually asks for."}
          </p>
        </div>
      </header>

      {!ladder && <NotMappedYet family={family} />}

      {ladder && (
        <>
          <div className="ds-card Growth-Law">
            <strong>How this ladder works</strong>
            <ul>
              {ladder.law.map((line, i) => <li key={i}>{line}</li>)}
            </ul>
          </div>

          {ladder.key === "sales" && <SalesLadder positionTitle={positionTitle} fullTime={fullTime} overrideLevel={employee.growth_rung_override} />}
          {ladder.key === "engineering" && <EngineeringLadder positionTitle={positionTitle} fullTime={fullTime} overrideLevel={employee.growth_rung_override} />}
        </>
      )}
    </section>
  );
}
